import React from 'react';

import { formatRelativeDateTime } from 'utils/dateUtils';

import {
  BidData,
  getBidDescription,
  getBidTypePrefix,
  formatBidPrice,
  getOwnershipBadge,
} from './bidUtils';

interface BidRowProps {
  bid: BidData;
  isSelected: boolean;
  isCounterOffer?: boolean;
  onClick: () => void;
  children?: React.ReactNode; // For action buttons
}

export const BidRow: React.FC<BidRowProps> = ({
  bid,
  isSelected,
  isCounterOffer = false,
  onClick,
  children,
}) => {
  const display = formatRelativeDateTime(bid.created_date);
  const bidDescription = getBidDescription(bid);
  const bidTypePrefix = getBidTypePrefix(bid, isCounterOffer);
  const priceDisplay = formatBidPrice(bid);
  const ownershipBadge = getOwnershipBadge(bid);

  // Selection styling
  const selectionClass = isSelected
    ? 'bg-blue-800 border-blue-500 border-l-4'
    : 'border-l border-gray-800 hover:bg-brand-950';

  // Grid layout - align with SellerBidReview (narrow first col, wider Price/Participation, date one line)
  const gridClass = isCounterOffer
    ? 'grid cursor-pointer grid-cols-[348px_280px_150px_1fr] items-center justify-between py-2 pl-[44px] border-l-2 border-gray-600'
    : 'grid cursor-pointer grid-cols-[380px_280px_150px_1fr] items-center justify-between py-2 pl-3';

  const statusStyles = {
    ACCEPTED: 'bg-green-600 text-green-100',
    REJECTED: 'bg-red-600 text-red-100',
  };

  return (
    <li className={`${gridClass} ${selectionClass}`} onClick={onClick}>
      {/* Bid Description */}
      <div className="flex items-center gap-2">
        <span className="text-l font-medium text-white">
          {bidTypePrefix} on {bidDescription}
        </span>
      </div>

      {/* Price and Ownership - single line */}
      <div className="flex min-w-0 items-center gap-3 whitespace-nowrap">
        <span className="text-l tracking-tight">Price</span>
        <span className="text-l text-gray-400">{priceDisplay}</span>
        {ownershipBadge && (
          <span className="rounded bg-purple-800 px-2 py-0.5 text-xs text-purple-200">
            {ownershipBadge}
          </span>
        )}
      </div>

      {/* Status and Actions */}
      <div className="flex items-center gap-2">
        {(bid.status === 'ACCEPTED' || bid.status === 'REJECTED') && (
          <span
            className={`rounded px-2 py-1 text-xs font-semibold ${statusStyles[bid.status as keyof typeof statusStyles]}`}
          >
            {bid.status}
          </span>
        )}
        {children}
      </div>

      {/* Date - single line */}
      <div className="flex min-w-0 items-center justify-end">
        <span className="whitespace-nowrap text-right text-sm text-gray-400">{display}</span>
      </div>
    </li>
  );
};
